import { type KeyboardEvent, type MouseEvent } from "react";

import { ChevronDownIcon } from "lucide-react";

import { cx, styled } from "simplydo/react/styled";

import {
  AccordionTitleIcon,
  AccordionTitleLabel,
  AccordionTitleContent,
  StyledAccordionTitle,
} from "./Accordion.styles";
import type { AccordionTitleProps } from "./Accordion.types";
import { useAccordionContext } from "./AccordionContext";

const AccordionTitleComponent = (props: AccordionTitleProps) => {
  const {
    active: activeProp,
    children,
    className,
    index,
    tabIndex,
    onKeyDown,
    label,
    labelPosition = "right",
    style,
    onClick,
  } = props;

  const ctx = useAccordionContext();
  const active =
    activeProp !== undefined
      ? activeProp
      : (ctx?.isIndexActive(index) ?? false);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const data: AccordionTitleProps = { ...props, active };
    onClick?.(event, data);
    ctx?.handleTitleClick(event, data);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (
      event.defaultPrevented ||
      event.target !== event.currentTarget ||
      (event.key !== "Enter" && event.key !== " ")
    ) {
      return;
    }
    event.preventDefault();
    handleClick(event as unknown as MouseEvent<HTMLDivElement>);
  };

  const classes = cx(active && "is-active", className);
  return (
    <StyledAccordionTitle
      className={cx(classes)}
      style={style}
      role="button"
      aria-expanded={active}
      tabIndex={tabIndex ?? 0}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
    >
      <AccordionTitleContent>
        <AccordionTitleIcon icon={ChevronDownIcon} />
        {label && labelPosition === "left" && (
          <AccordionTitleLabel $position={labelPosition}>
            {label}
          </AccordionTitleLabel>
        )}
        {children}
      </AccordionTitleContent>
      {label && labelPosition === "right" && (
        <AccordionTitleLabel $position={labelPosition}>
          {label}
        </AccordionTitleLabel>
      )}
    </StyledAccordionTitle>
  );
};

export const AccordionTitle = styled(AccordionTitleComponent)``;
